import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Divider from '@mui/material/Divider';
import { Text, Theme, Icons, List, i18n, tokens } from '@simplybenefits/sb-ui-library';
import Avatar from "react-avatar";

import SupportModal from '../containers/Support/index.js';
import { setCurrentSidebarItem } from '../actions/user.js';
import * as dates from '@simplybenefits/sb-dates';
import SetMaximumsModal from '../containers/SpendingAccounts/SetMaximumsModal.js';
import Icon from '@mui/material/Icon';

import pocketPillsImage from '../assets/pocketpills-icon.png';

/**
 *
 * @param {*} props
 * @returns custom link component to avoid repeating styles
 */
const LinkStyled = (props) => (
    <Link style={{
        textDecoration: 'none',
        marginTop: "2.5px",
        marginBottom: "2.5px",
        display: "flex",
        justifyContent: "center",
        cursor: 'pointer',
        backgroundColor: props.selected && Theme.colors.backgroundGrey
    }}
    to={ props.to }
    onClick={ props.onClick }>
        <ListItem button sx={{
            py: "12px",
            px: "16px",
            "&:hover": {
                backgroundColor: props.selected && Theme.colors.lightHoverGrey
            }
        }}>
            { props.children }
        </ListItem>
    </Link>
);

/**
 *
 * @returns
 */
const Sidebar = (props) => {
    const [ state, setState ] = useState({
        selectedScreen: 'dashboard',
        supportModal: false
    });

    let selectedScreen = '';
    const href = window.location.href;

    useEffect(() => {
        if (state.supportModal) {
            selectedScreen = 'support';
        } else if (href.includes('card')) {
            selectedScreen = 'benefits-card';
        } else if (href.includes('spending-accounts')) {
            selectedScreen = 'spending-accounts';
        } else if (href.includes('my-account')) {
            selectedScreen = 'account';
        } else if (href.includes('claims')) {
            selectedScreen = 'claims';
        } else if (href.includes('usage')) {
            selectedScreen = 'usage';
        } else if (href.includes('coverage')) {
            selectedScreen = 'coverage';
        } else if (href.includes('invoices')) {
            selectedScreen = 'invoices';
        } else if (href.includes('marketplace')) {
            selectedScreen = 'marketplace';
        } else if (href.includes('notifications')) {
            selectedScreen = 'notifications';
        } else if (href.includes('resources')) {
            selectedScreen = 'resources';
        } else if (href.includes('files')) {
            selectedScreen = 'files';
        } else {
            selectedScreen = 'dashboard';
        }

        setState({
            ...state,
            selectedScreen
        });

    }, [ href, state.supportModal ]);

    /**
     *
     * @param {*} link
     */
    const _navigateTo = (link) => {
        window.open(link, "_blank");
    };

    /**
     *
     * @param {*} selectedScreen
     */
    const _selectItem = (screen) => {
        setState({
            ...state,
            selectedScreen: screen
        });

        props.dispatch(setCurrentSidebarItem(false));

        if (props.closeSideBar) {
            props.closeSideBar();
        }
    };

    /**
     *
     */
    const _toggleSupport = () => {
        setState({
            ...state,
            supportModal: !state.supportModal
        });
    };

    const { whitelabel, coverage, user, onlySpendingAccountActive, noClaims } = props;
    const email = user.email;
    const isMarketplaceOnly = user.isMarketplaceOnly;
    const isDependent = user.isDependent;
    const name = `${ user.firstname } ${ user.lastname }`;
    const spendingAccounts = coverage.spendingAccounts;
    const hideMarketplace = !user.allowOptionalBenefits && !user.allowVoluntaryBenefits;

    const pendingSpendingAccountMaximumSettings = !isDependent && Number(spendingAccounts.meta.pendingMaximumSetting) === 1;
    let openSetSpendingAccountMaximums = false;
    const now = dates.getUTC();

    const styles = {
        notificationBadge: {
            textDecoration: 'none',
            color: Theme.colors.mainTextGrey,
            zIndex: 999
        },
        gearBadge: {
            flex: 1,
            textDecoration: 'none',
            color: Theme.colors.mainTextGrey,
            zIndex: 999
        },
        downloadBtnContainer: {
            display: 'flex',
            flex: 1,
            justifyContent: 'flex-end',
            marginBottom: 10
        },
        pocketpills: {
            height: 30,
            width: 'auto',
            paddingRight: 20
        },
        sidebar: {
            backgroundColor: Theme.colors.surface,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%'
        },
        sidebarHeader: {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            width: "100%",
            minHeight: 250,
            padding: 10
        },
        sidebarHeaderWatermark: {
            alignSelf: 'flex-end',
            position: 'absolute',
            zIndex: 9,
            bottom: 0,
            right: 0,
            width: 180,
            opacity: 0.4
        },
        sidebarBody: {
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: Theme.colors.surface,
            flex: 1,
            padding: '10px 30px',
            overflowY: 'auto'
        },
        icon: {
            color: Theme.colors.mainTextGrey
        },
        perksIcon: {
            color: '#E6B422'
        }
    };

    if (!isDependent && pendingSpendingAccountMaximumSettings && now < spendingAccounts.meta.settingMaximumsExpiresOn) {
        openSetSpendingAccountMaximums = true;
    }

    return (
        <div style={ styles.sidebar }>
            <div style={ styles.sidebarHeader }>
                <Avatar
                    name={name}
                    size="100"
                    textSizeRatio={3}
                    round={true}
                    color={whitelabel.primaryColor || Theme.colors.skyBlue}
                    style={{ marginBottom: 20 }}
                />

                <Text medium>{ `${ i18n.t(tokens.labels.dashboard.welcome) },`}</Text>
                <Text large bold>{ name }</Text>
                <Text medium>{ email }</Text>
            </div>

            <Divider variant="middle" style={{ margin: 21 }}/>

            <div style={ styles.sidebarBody }>
                <List style={{ display: "flex", flexDirection: "column" }}>
                    <LinkStyled
                        to={'/'}
                        onClick={() => _selectItem('dashboard')}
                        selected={ state.selectedScreen === 'dashboard' }
                    >
                        <ListItemIcon>
                            <Icon style={ styles.icon }>
                                { Icons.Dashboard }
                            </Icon>
                        </ListItemIcon>
                        <Text avMedium >{ i18n.t(tokens.titles.dashboard) }</Text>
                    </LinkStyled>

                    {
                        !isMarketplaceOnly &&
                            <LinkStyled
                                to={'/benefits-card'}
                                onClick={() => _selectItem('benefits-card')}
                                selected={ state.selectedScreen === 'benefits-card' }
                            >
                                <ListItemIcon>
                                    <Icon style={ styles.icon }>
                                        { Icons['Benefits Card'] }
                                    </Icon>
                                </ListItemIcon>
                                <Text avMedium>{ i18n.t(tokens.titles["benefits card"]) }</Text>
                            </LinkStyled>
                    }

                    {
                        !isMarketplaceOnly && !isDependent && !user.isSurvivorBenefitParent && (spendingAccounts.health.hasBenefit > 0 || spendingAccounts.lifestyle.hasBenefit > 0) &&
                            <LinkStyled
                                to={'/spending-accounts'}
                                onClick={() => _selectItem('spending-accounts')}
                                selected={ state.selectedScreen === 'spending-accounts' }
                            >
                                <ListItemIcon>
                                    <Icon style={ styles.icon }>
                                        { Icons['Spending Accounts'] }
                                    </Icon>
                                </ListItemIcon>
                                <Text avMedium>{ i18n.t(tokens.titles.spendingAccounts) }</Text>
                            </LinkStyled>
                    }

                    {
                        (!isMarketplaceOnly && (!onlySpendingAccountActive || !noClaims)) &&
                            <LinkStyled
                                to={'/claims'}
                                onClick={() => _selectItem('claims')}
                                selected={ state.selectedScreen === 'claims' }
                            >
                                <ListItemIcon>
                                    <Icon style={ styles.icon }>
                                        { Icons.Claims }
                                    </Icon>
                                </ListItemIcon>
                                <Text avMedium>{ i18n.t(tokens.titles.claims) }</Text>
                            </LinkStyled>
                    }

                    {
                        (!isMarketplaceOnly && (!onlySpendingAccountActive || !noClaims)) &&
                            <LinkStyled
                                to={'/usage'}
                                onClick={() => _selectItem('usage')}
                                selected={ state.selectedScreen === 'usage' }
                            >
                                <ListItemIcon>
                                    <Icon style={ styles.icon }>
                                        { Icons['My Usage'] }
                                    </Icon>
                                </ListItemIcon>
                                <Text avMedium>{ i18n.t(tokens.titles.usage) }</Text>
                            </LinkStyled>
                    }

                    {
                        !isMarketplaceOnly &&
                            <LinkStyled
                                to={'/coverage'}
                                onClick={() => _selectItem('coverage')}
                                selected={ state.selectedScreen === 'coverage' }
                            >
                                <ListItemIcon>
                                    <Icon style={ styles.icon }>
                                        { Icons.Coverage }
                                    </Icon>
                                </ListItemIcon>
                                <Text avMedium>{ i18n.t(tokens.titles.coverage) }</Text>
                            </LinkStyled>
                    }

                    {
                        !isDependent && !hideMarketplace &&
                            <LinkStyled
                                to={'/marketplace'}
                                onClick={() => _selectItem('marketplace')}
                                selected={ state.selectedScreen === 'marketplace' }
                            >
                                <ListItemIcon>
                                    <Icon style={ styles.icon }>
                                        { Icons.Marketplace }
                                    </Icon>
                                </ListItemIcon>
                                <Text avMedium>{ i18n.t(tokens.titles.marketplace) }</Text>
                            </LinkStyled>
                    }

                    {
                        !isDependent &&
                            <LinkStyled
                                to={'/files'}
                                onClick={() => _selectItem('files')}
                                selected={ state.selectedScreen === 'files' }
                            >
                                <ListItemIcon>
                                    <Icon style={ styles.icon }>
                                        { Icons.Files }
                                    </Icon>
                                </ListItemIcon>
                                <Text avMedium>{ i18n.t(tokens.titles.files) }</Text>
                            </LinkStyled>
                    }

                    {
                        user.divisionPaymentType && user.divisionPaymentType.toUpperCase() === 'MEMBER' &&
                            <LinkStyled
                                to={'/invoices'}
                                onClick={() => _selectItem('invoices')}
                                selected={ state.selectedScreen === 'invoices' }
                            >
                                <ListItemIcon>
                                    <Icon style={ styles.icon }>
                                        { Icons.Invoices }
                                    </Icon>
                                </ListItemIcon>
                                <Text avMedium>{ i18n.t(tokens.titles.invoices) }</Text>
                            </LinkStyled>
                    }

                    <LinkStyled
                        to={'/perks'}
                        onClick={() => _selectItem('perks')}
                        selected={ state.selectedScreen === 'perks' }
                    >
                        <ListItemIcon>
                            <Icon style={ styles.perksIcon }>
                                { Icons.Star }
                            </Icon>
                        </ListItemIcon>
                        <Text avMedium>{ i18n.t(tokens.labels.perks.simplyPerks) }</Text>
                    </LinkStyled>

                    <Divider variant="middle" style={{ margin: 10 }}/>

                    {
                        !isMarketplaceOnly &&
                            <LinkStyled
                                to={'/resources'}
                                onClick={() => _selectItem('resources')}
                                selected={ state.selectedScreen === 'resources' }
                            >
                                <ListItemIcon>
                                    <Icon style={ styles.icon }>
                                        { Icons.Resources }
                                    </Icon>
                                </ListItemIcon>
                                <Text avMedium>{ i18n.t(tokens.titles.resources) }</Text>
                            </LinkStyled>
                    }

                    <div style={{ textDecoration: 'none',
                        marginTop: "2.5px",
                        marginBottom: "2.5px",
                        display: "flex",
                        justifyContent: "center",
                        alignContent: "center",
                        cursor: 'pointer',
                        backgroundColor: state.selectedScreen === 'support' && Theme.colors.backgroundGrey }}
                    >
                        <ListItem id="toggle-support" button onClick={_toggleSupport} sx={{
                            py: "12px",
                            px: "16px"
                        }}>
                            <ListItemIcon>
                                <Icon style={ styles.icon }>
                                    { Icons.Support }
                                </Icon>
                            </ListItemIcon>
                            <Text avMedium>{ i18n.t(tokens.titles.support) }</Text>
                        </ListItem>
                    </div>
                </List>
            </div>

            {
                openSetSpendingAccountMaximums &&
                    <SetMaximumsModal
                        meta={ spendingAccounts.meta }
                        whitelabel={ whitelabel || {} }
                    />
            }

            {
                state.supportModal &&
                    <SupportModal
                        open={ state.supportModal}
                        close={() => _toggleSupport()}
                    />
            }
        </div>
    );
};

Sidebar.propTypes = {
    user: PropTypes.object
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    user: state.user.meta,
    coverage: state.coverage.data,
    currentSidebarItem: state.user.currentSidebarItem,
    whitelabel: state.whitelabel,
    noClaims: state.user.noClaims,
    onlySpendingAccountActive: state.user.onlySpendingAccountActive
});

export default connect(mapStateToProps)(Sidebar);